<template>
  <div class="detail-panel">
    <div class="detail-header">
      <button class="btn btn-secondary btn-sm" @click="$emit('close')">✕ Close</button>
      <div class="detail-header-actions">
        <button class="btn btn-edit btn-sm"   @click="$emit('edit', emp)">✏️ Edit</button>
        <button class="btn btn-delete btn-sm" @click="$emit('delete', emp)">🗑️</button>
      </div>
    </div>

    <div class="detail-hero">
      <employee-avatar :emp="emp" class="detail-avatar" />
      <h2 class="detail-name">{{ displayName }}</h2>
      <p class="detail-position">{{ emp.position ? emp.position.name : '—' }}</p>
      <div class="detail-badges">
        <status-badge :status="emp.status" />
        <site-badge :name="emp.site ? emp.site.name : null" />
      </div>
    </div>

    <div class="detail-body">

      <div class="detail-section">
        <div class="detail-section-title">Personal data</div>
        <div v-for="row in personalRows" :key="row[0]" class="detail-row">
          <span class="detail-key">{{ row[0] }}</span>
          <span class="detail-val">{{ row[1] }}</span>
        </div>
      </div>

      <div class="detail-section">
        <div class="detail-section-title">Documents</div>
        <div v-for="row in documentRows" :key="row[0]" class="detail-row">
          <span class="detail-key">{{ row[0] }}</span>
          <span class="detail-val">{{ row[1] }}</span>
        </div>
      </div>

      <div class="detail-section">
        <div class="detail-section-title">Employment</div>
        <div v-for="row in employmentRows" :key="row[0]" class="detail-row">
          <span class="detail-key">{{ row[0] }}</span>
          <span class="detail-val">{{ row[1] }}</span>
        </div>
      </div>

      <div v-if="emp.comment" class="detail-section">
        <div class="detail-section-title">Comment</div>
        <p class="detail-comment">{{ emp.comment }}</p>
      </div>

    </div>
  </div>
</template>

<script>
import EmployeeAvatar from './EmployeeAvatar.vue';
import SiteBadge      from './SiteBadge.vue';
import StatusBadge    from './StatusBadge.vue';

function getAddress(emp) {
  if (!emp.addresses) return null;
  if (typeof emp.addresses === 'string') return emp.addresses;
  // Real API: addresses is an array of { address: '...' }
  if (Array.isArray(emp.addresses)) {
    const found = emp.addresses.find(a => a && a.address);
    return found ? found.address : null;
  }
  return null;
}

function getPhone(emp) {
  if (!emp.contact_data) return null;
  if (Array.isArray(emp.contact_data)) {
    const found = emp.contact_data.find(c => c && c.phone_number);
    return found ? found.phone_number : null;
  }
  return null;
}

export default {
  name: 'DetailPanel',
  components: { EmployeeAvatar, SiteBadge, StatusBadge },

  props: {
    emp: { type: Object, required: true },
  },

  computed: {
    displayName() {
      // Use backend-provided fullName first, then fall back to field concatenation
      if (this.emp.fullName) return this.emp.fullName;
      const surname = this.emp.surname        || (this.emp.user && this.emp.user.surname)        || '';
      const first   = this.emp.christian_name1 || (this.emp.user && this.emp.user.christian_name1) || '';
      return `${surname} ${first}`.trim() || '—';
    },

    email() {
      return (this.emp.user && this.emp.user.email) ? this.emp.user.email : null;
    },

    personalRows() {
      return [
        ['Email',        this.email],
        ['Birth place',  this.emp.birth_place],
        ['Birth date',   this.emp.birth_date],
        ['Birth name',   this.emp.birth_name],
        ['Mother name',  this.emp.mother_name],
        ['Address',      getAddress(this.emp)],
        ['Phone',        getPhone(this.emp)],
      ].filter(r => r[1]);
    },

    documentRows() {
      return [
        ['Identity card no.',  this.emp.identity_card_number],
        ['Address card no.',   this.emp.address_card_number],
        ['Tax identifier',     this.emp.vat_number],
        ['Social Sec. no.',    this.emp.insurance_number],
      ].filter(r => r[1]);
    },

    employmentRows() {
      return [
        ['Department',    this.emp.department ? this.emp.department.name : null],
        ['Education',     this.emp.education  ? this.emp.education.name  : null],
        ['Start date',    this.emp.work_start],
        ['End date',      this.emp.work_end],
      ].filter(r => r[1]);
    },
  },
};
</script>

<style scoped>
.detail-panel {
  position: fixed;
  right: 0; top: 0; bottom: 0;
  width: min(420px, 90vw);
  background: #fff;
  box-shadow: -10px 0 40px rgba(0,0,0,.10);
  z-index: 50;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}
.detail-header         { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px 16px; }
.detail-header-actions { display: flex; gap: 8px; }
.detail-hero           { text-align: center; padding: 0 24px 20px; border-bottom: 1px solid #F0F2F7; }
.detail-avatar         { width: 72px !important; height: 72px !important; border-radius: 20px !important; font-size: 24px !important; margin: 0 auto 12px; }
.detail-name           { font-size: 20px; font-weight: 700; color: #1a1a2e; margin: 0 0 4px; }
.detail-position       { font-size: 13px; color: #9DA5C0; margin: 0; }
.detail-badges         { display: flex; gap: 8px; justify-content: center; margin-top: 10px; }
.detail-body           { padding: 20px 24px; flex: 1; }
.detail-section        { margin-bottom: 20px; }
.detail-section-title  { font-size: 10px; font-weight: 700; color: #7B82A0; text-transform: uppercase; letter-spacing: .1em; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 1px solid #F0F2F7; }
.detail-row            { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; padding: 5px 0; }
.detail-key            { font-size: 12px; color: #9DA5C0; font-weight: 500; min-width: 130px; }
.detail-val            { font-size: 13px; color: #1a1a2e; font-weight: 600; text-align: right; }
.detail-comment        { font-size: 13px; color: #4A5068; line-height: 1.6; margin: 0; }

.btn         { cursor: pointer; border: none; font-family: inherit; font-weight: 600; border-radius: 10px; transition: filter .15s, transform .15s; display: inline-flex; align-items: center; gap: 6px; }
.btn:hover   { filter: brightness(.92); transform: translateY(-1px); }
.btn-secondary { background: #F0F4FA; color: #4A5068; }
.btn-edit    { background: #EEF1FF; color: #5B6BFF; }
.btn-delete  { background: #FEF0F0; color: #E05252; }
.btn-sm      { padding: 6px 12px; font-size: 12px; }
</style>